/* eslint-disable @typescript-eslint/no-explicit-any */

import { Box, Spinner, Stack, Text } from '@chakra-ui/react';
import { UploadSimple, File, X, CheckCircle } from '@phosphor-icons/react';
import { useState } from 'react';

import { fetchAPI } from '@/api/fetch';
import { apiAdminExtractQuestionBank } from '@/api/services';
import { useAdminAnalysis } from '@/store/adminAnalysis';
import { MAX_RESUME_FILE_SIZE, MAX_RESUME_FILE_SIZE_MB } from '@/config/constants';

export default function QuestionBankExtractorSection() {
  const fileSizeLimit = MAX_RESUME_FILE_SIZE;
  const [fileUploading, setFileUploading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const {
    questionBankFile,
    questionBank,
    setQuestionBankFile,
    setQuestionBank,
    clearQuestionBankFile,
    clearQuestionBank
  } = useAdminAnalysis();

  const getFileSize = (size?: number): string => {
    if (!size) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return `${(size / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const handleFileUpload = async (file: File) => {
    setQuestionBankFile(file);
    setFileUploading(true);
    clearQuestionBank();
    setFileError(null);

    if (file.size > fileSizeLimit) {
      setFileError(`File size exceeds ${MAX_RESUME_FILE_SIZE_MB} MB`);
      setFileUploading(false);
      return;
    }

    try {
      const response = await fetchAPI<QuestionBankExtractionData>({
        request: () => apiAdminExtractQuestionBank({ payload: { file } })
      });

      if (response.success) {
        setQuestionBank(response.data);
      } else {
        handleFileError('Unable to extract question bank', response.message);
      }
    } catch (error) {
      handleFileError('Unable to extract question bank', error);
    } finally {
      setFileUploading(false);
    }
  };

  const handleFileError = (message: string, error: any) => {
    console.error(message, error);
    setFileError(message);
  };

  const onUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  };

  const resetFileState = () => {
    clearQuestionBankFile();
    clearQuestionBank();
    setFileError(null);
  };

  return (
    <Stack gap="24px" width="100%">
      <Stack gap="16px" align="flex-start" width="100%">
        {questionBankFile || fileUploading ? (
          <Box display="flex" alignItems="center" width="100%">
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              width="150px"
              height="150px"
              borderRadius="12px"
              border="1px solid"
              borderColor={fileError ? 'error.500' : 'primary.100'}
              bg="primary.50"
            >
              {fileUploading ? (
                <>
                  <Spinner size="lg" borderWidth="4px" color="primary.900" />
                  <Box mt="8px" fontSize="16px" color="neutrals.900" textAlign="center">
                    Uploading
                  </Box>
                </>
              ) : (
                <File size={64} color="#4e76ff" />
              )}
            </Box>
            <Box ml="32px" color="neutrals.900">
              <Box display="flex" alignItems="center" mb="4px">
                {!fileUploading && (
                  <Box mr="12px">
                    <CheckCircle size={24} color="#4e76ff" weight="fill" />
                  </Box>
                )}
                <Box fontSize="18px" color="neutrals.800" mr="16px">
                  {questionBankFile?.name}
                </Box>
                {!fileUploading && (
                  <X size={16} color="#545454" onClick={resetFileState} cursor="pointer" />
                )}
              </Box>
              <Box fontSize="16px" color={fileError ? 'error.700' : 'neutrals.500'}>
                {getFileSize(questionBankFile?.size)}
              </Box>
              {fileError && (
                <Box fontSize="16px" color="error.700" mt="4px">
                  {fileError}
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <Box
            w="100%"
            h="200px"
            border="1px dashed"
            borderColor="neutrals.300"
            borderRadius="12px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            _hover={{ borderColor: 'neutrals.300' }}
            bg="neutrals.50"
          >
            <Box
              position="relative"
              width="100%"
              height="200px"
              border="2px dashed"
              borderColor="neutrals.300"
              borderRadius="12px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              _hover={{ borderColor: 'neutrals.300' }}
              bg="neutrals.50"
            >
              <input
                id="fileInput"
                type="file"
                accept=".pdf,.txt"
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer'
                }}
                onChange={onUploadFile}
              />
              <Box display="flex" flexDirection="column" alignItems="center">
                <UploadSimple size={60} color="#404040" />
                <Box textAlign="center" color="neutrals.900">
                  Drop file here or{' '}
                  <Box as="span" color="primary.900">
                    click to upload
                  </Box>
                </Box>
                <Box color="neutrals.500" fontSize="14px">
                  Upload PDF, or TXT file types ({MAX_RESUME_FILE_SIZE_MB} MB max)
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Stack>
      <Text fontSize="18px" color="neutrals.900" fontWeight="semibold">
        Extracted Question Bank
      </Text>
      {questionBank ? (
        <QuestionBankSection questionBank={questionBank} />
      ) : (
        <Text>No question bank extracted yet.</Text>
      )}
    </Stack>
  );
}

const QuestionBankSection = ({ questionBank }: { questionBank: QuestionBankExtractionData }) => {
  return (
    <Stack gap="32px">
      <Box
        p="20px"
        border="1px solid"
        borderColor="neutrals.200"
        borderRadius="12px"
        bg="neutrals.50"
        boxShadow="sm"
      >
        <Text fontSize="xl" fontWeight="bold" color="primary.900" mb="4px">
          {questionBank.job_title || 'Untitled Position'}
        </Text>
        <Text fontSize="md" color="neutrals.700">
          {questionBank.description || 'No description provided.'}
        </Text>
      </Box>
      <Stack gap="20px">
        <Text fontSize="lg" fontWeight="semibold" color="primary.900">
          Questions ({questionBank.questions.length})
        </Text>
        {questionBank.questions.length === 0 ? (
          <Text color="neutrals.500" fontStyle="italic">
            No questions extracted.
          </Text>
        ) : (
          <Stack gap="16px">
            {questionBank.questions.map((question, index) => (
              <Box
                key={index}
                p="18px"
                border="1px solid"
                borderColor="primary.100"
                borderRadius="10px"
                bg="white"
                boxShadow="xs"
                display="flex"
                alignItems="flex-start"
                gap="12px"
              >
                <Box
                  minW="36px"
                  h="36px"
                  bg="primary.50"
                  color="primary.900"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontWeight="bold"
                  fontSize="md"
                  mt="2px"
                >
                  Q{index + 1}
                </Box>
                <Text fontSize="md" color="neutrals.900" lineHeight="1.6">
                  {question}
                </Text>
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
